/**
 * True on typical local/dev hostnames. Prefer `window.location.origin` so links and API
 * match the current tab (localhost, Lando `*.lh`, etc.) and ignore REACT_APP_* that target prod.
 */
export function isLocalDevHost(hostname) {
  if (!hostname) return false;
  const h = String(hostname).toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0' || h === '::1' || h === '[::1]') {
    return true;
  }
  if (h.endsWith('.localhost')) return true;
  if (h.endsWith('.lh')) return true;
  return false;
}

function stripSlash(u) {
  return String(u || '').replace(/\/$/, '');
}

/**
 * Site origin (app URL). Production build: REACT_APP_SITE_URL (e.g. https://crawlervision.com).
 */
export function getSiteUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const h = window.location.hostname || '';
    if (isLocalDevHost(h)) {
      return stripSlash(window.location.origin);
    }
  }
  const url = process.env.REACT_APP_SITE_URL || 'https://crawlervision.com';
  return stripSlash(url);
}

/**
 * Single source of truth for backend/API URL.
 * Production build: REACT_APP_BACKEND_URL (e.g. https://api.crawlervision.com).
 * Local dev: same as browser origin so /api and Traefik / Lando routes match the open tab.
 */
export function getBackendUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const h = window.location.hostname || '';
    if (isLocalDevHost(h)) {
      return stripSlash(window.location.origin);
    }
  }
  if (process.env.REACT_APP_BACKEND_URL) {
    return stripSlash(process.env.REACT_APP_BACKEND_URL);
  }
  if (typeof window !== 'undefined') {
    try {
      if (new URL(getSiteUrl()).origin === window.location.origin) return getSiteUrl();
    } catch (_) {}
  }
  return '';
}

/** API base path (backend URL + /api) */
export function getApiBase() {
  return `${getBackendUrl()}/api`;
}

/**
 * Base URL for report access (HTML/PDF/JSON links).
 * Production build: REACT_APP_REPORTS_URL (e.g. https://reports.crawlervision.com).
 */
export function getReportsBaseUrl() {
  if (typeof window !== 'undefined' && isLocalDevHost(window.location.hostname)) {
    return getBackendUrl();
  }
  if (process.env.REACT_APP_REPORTS_URL) {
    return stripSlash(process.env.REACT_APP_REPORTS_URL);
  }
  return getBackendUrl();
}

/**
 * Report links from API may point at another host (e.g. http://localhost:8001) while the
 * app runs on cv.lh. For local dev, rebuild using pathname + getReportsBaseUrl() so the
 * open tab origin wins. Path-only share URLs (/) are always prefixed with getReportsBaseUrl().
 */
export function resolveReportShareUrl(absoluteOrRelative) {
  if (absoluteOrRelative == null || absoluteOrRelative === '') return absoluteOrRelative;
  const s = String(absoluteOrRelative).trim();
  if (s.startsWith('/')) {
    return `${stripSlash(getReportsBaseUrl())}${s}`;
  }
  if (typeof window === 'undefined' || !isLocalDevHost(window.location.hostname)) {
    return s;
  }
  try {
    const parsed = new URL(s);
    if (parsed.pathname.startsWith('/api/seo/report')) {
      return `${stripSlash(getReportsBaseUrl())}${parsed.pathname}${parsed.search || ''}${parsed.hash || ''}`;
    }
  } catch (_) { /* use as-is */ }
  return s;
}

/**
 * Analyzer landing URL (?page=analyzer). Prefer current browser origin on local dev; production
 * may set REACT_APP_ANALYZER_URL.
 */
export function getAnalyzerUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    if (isLocalDevHost(window.location.hostname)) {
      return `${window.location.origin}/?page=analyzer`;
    }
  }
  const env = process.env.REACT_APP_ANALYZER_URL;
  if (env && String(env).trim()) {
    return stripSlash(env);
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/?page=analyzer`;
  }
  return `${getSiteUrl()}/?page=analyzer`;
}

/**
 * Homepage → Analyzer with prefilled URL and one-shot autostart.
 * Uses the URL hash (not the querystring) so App can strip it after read / when analysis
 * ends without leaving a test URL in the address for the next refresh.
 * Format: #url=...&autostart=1
 */
export function getAnalyzerHandoffWithUrl(cleanUrl) {
  const base = getAnalyzerUrl();
  const hp = new URLSearchParams();
  hp.set('url', cleanUrl);
  hp.set('autostart', '1');
  const frag = hp.toString();
  try {
    const b = new URL(base);
    return `${b.origin}${b.pathname}${b.search}#${frag}`;
  } catch (_) {
    const [beforeHash] = String(base).split('#');
    return `${beforeHash}#${frag}`;
  }
}

/** Contact email for general enquiries */
export const CONTACT_EMAIL = 'support@crawlervision.com';

/** Demo / sales booking URL (placeholder — update when calendaring is set up) */
export const DEMO_URL = 'mailto:premium@crawlervision.com?subject=Crawler%20Vision%20Demo%20Request';

export default {
  getSiteUrl,
  getBackendUrl,
  getApiBase,
  getReportsBaseUrl,
  resolveReportShareUrl,
  getAnalyzerUrl,
  getAnalyzerHandoffWithUrl,
  isLocalDevHost,
};
